import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import { App, GQL } from '@gin-betting/core';
import restrictions from '@gin-betting/restrictions';
import { createStructuredSelector } from 'reselect';

import { Modules, useAsyncModule } from 'async-modules';
import ui from 'modules/ui';
import type { State } from 'types';
import { formatDecimalOdd } from 'utils';
import {
    useChangeStake,
    useExpressSystemErrors,
    usePlaceBet,
} from '../../../hooks';
import { RestrictionsContext } from '../../../restrictions';
import { makeSelectIsFreebet } from '../../../selectors';

const {
    selectors: { selectBetslipBetWithStatus, selectBetslipBetTotals },
} = betslip;
const {
    selectors: { selectOddFormat },
} = ui;

const {
    selectors: { selectLowerMaxBetBetslipBet },
} = restrictions;

type Output = {
    changeStakeHandler: (stake: string) => void;
    toggleFreeBetHandler: (amount: string | null) => void;
    totals: ReturnType<typeof selectBetslipBetTotals>;
    maxStake: string;
    errorMsgs: Array<string | string[]>;
    betType: GQL.BetType;
    betslipOddIds: string[];
    isSuspended: boolean;
    formattedTotalOdd: ReturnType<typeof formatDecimalOdd>['component'];
    isDecimal: boolean;
    isFreebetSelected: boolean;
    stake: string;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
} | null;

interface Input {
    betslipBetId: string;
}

const useExpressPart = ({ betslipBetId }: Input): Output => {
    const { state: restrictionsState } = useContext(RestrictionsContext);
    const freebetModule = useAsyncModule('freebet');
    const selectProps = useMemo(() => makeSelectProps(freebetModule), [
        freebetModule,
    ]);
    const changeStakeHandler = useChangeStake({ betslipBetId });
    const {
        betslipBetWithStatus,
        totals,
        maxBet,
        oddFormat,
        isFreebet,
    } = useSelector((state: State) =>
        selectProps(state, { betslipBetId, restrictionsState })
    );

    const { errorMsgs } = useExpressSystemErrors({
        betslipBetId,
        isFreebetSelected: isFreebet,
    });

    const toggleFreeBetHandler = useCallback(
        (amount: string | null) => {
            changeStakeHandler(amount || '');
        },
        [changeStakeHandler]
    );

    if (!betslipBetWithStatus) return null;

    const {
        mode: betType,
        status,
        stake,
        betslipOddIds,
    } = betslipBetWithStatus;

    const { onKeyDown } = usePlaceBet({
        totalStake: +stake,
        betslipBetId,
    });

    const maxStake = isFreebet || !maxBet ? '' : maxBet;
    const { totalOdd } = totals;

    const { component: formattedTotalOdd } = formatDecimalOdd(
        totalOdd || 0,
        oddFormat
    );

    const isProcessing = status === App.BetslipStatus.PROCESSING;
    const isSuccess = status === App.BetslipStatus.SUCCESS;
    const isSuspended = isProcessing || isSuccess;
    const isDecimal = oddFormat === App.OddFormat.Decimal;

    return {
        changeStakeHandler,
        toggleFreeBetHandler,
        totals,
        maxStake,
        errorMsgs,
        betType,
        betslipOddIds: betslipOddIds.toArray(),
        isSuspended,
        formattedTotalOdd,
        isDecimal,
        isFreebetSelected: isFreebet,
        stake,
        onKeyDown,
    };
};

const makeSelectProps = (
    freebetModule: Modules['freebet']['default'] | null
) => {
    return createStructuredSelector({
        betslipBetWithStatus: selectBetslipBetWithStatus,
        totals: selectBetslipBetTotals,
        maxBet: selectLowerMaxBetBetslipBet,
        oddFormat: selectOddFormat,
        isFreebet: makeSelectIsFreebet(freebetModule),
    });
};

export default useExpressPart;
